import { createApp } from 'vue';
import * as Sentry from '@sentry/vue';
import '@/assets/styles/index.scss';
import '@zoomcatalog/design-system/dist/main.css';
import '@zoomcatalog/design-templates/dist/index.css';
import '@zoomcatalog/core-ui/dist/main.css';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { useSnifferServices } from '@zoomcatalog/composable';
import axios from 'axios';
import App from './App.vue';
import { setBaseUrl } from './config/axios';
import sentrySettings from './config/sentry';
import {
  createViewerI18n,
  setLanguage,
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
} from './i18n';
import type { SupportedLocale } from './i18n';
import router from './router';
import { settings } from './settings/DynamicSettings';
import { rootStore, useCommit } from './store/rootStore';

const LANGUAGE_STORAGE_KEY = 'zoomcatalog-language';

function getInitialLanguage(): SupportedLocale {
  const urlParams = new URLSearchParams(window.location.search);
  const langParam = urlParams.get('lang');

  if (langParam && SUPPORTED_LOCALES.includes(langParam as SupportedLocale)) {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, langParam);
    return langParam as SupportedLocale;
  }

  const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (
    savedLanguage &&
    SUPPORTED_LOCALES.includes(savedLanguage as SupportedLocale)
  ) {
    return savedLanguage as SupportedLocale;
  }

  return DEFAULT_LOCALE;
}

async function prepare() {
  const env = import.meta.env.MODE;

  if (settings.ENABLE_MSW) {
    const { worker } = await import('./mocks/browser');
    worker.start({ onUnhandledRequest: 'bypass' });
    setBaseUrl(`${window.origin}/api`);
  } else {
    const commit = useCommit();
    const snifferService = await useSnifferServices({
      httpGetRequest: axios.get,
      snifferQuery: {
        environment: import.meta.env.MODE as any,
        servicesList: [
          'quickcanvas',
          'auth',
          'companies',
          'users',
          'assets',
          'sso',
        ],
      },
    });
    commit({
      type: 'sniffer/setServicesEndpoints',
      payload: snifferService.baseUrls,
    });
    setBaseUrl(`${snifferService.baseUrls.QC_API_ENDPOINT}canvas/v1`);
  }

  return env;
}

prepare().then(async (env) => {
  const initialLanguage = getInitialLanguage();
  const i18n = createViewerI18n();

  setLanguage(initialLanguage);

  try {
    const { createZoomI18n, setLanguage: setSharedLanguage } = await import(
      '@zoomcatalog/shared'
    );
    createZoomI18n();
    setSharedLanguage(initialLanguage);
  } catch (error) {
    // Silently fail if shared package doesn't support i18n
  }

  const app = createApp(App);
  if (!['development', 'testing'].includes(env)) {
    Sentry.init({ app, ...sentrySettings });
  }
  app.use(router).use(rootStore).use(i18n).use(VueQueryPlugin).mount('#root');
});
